/** Stable keys stored in businesses.admin_ui_labels (must match backend whitelist). */
export type AdminUiLabelKey =
  | 'nav_dashboard'
  | 'nav_assets'
  | 'nav_templates'
  | 'nav_communications'
  | 'nav_reports'
  | 'nav_push_notifications'
  | 'nav_settings'
  | 'nav_calendar'
  | 'nav_calendar_bookings'
  | 'nav_calendar_settings'
  | 'nav_safety_checks'
  | 'nav_loan_checks'
  | 'nav_delivery_checks'
  | 'nav_location_checks'
  | 'nav_locations'
  | 'nav_dvla'
  | 'nav_incidents'
  | 'nav_hr'
  | 'nav_mot_tax'
  | 'nav_vehicle_evaluation'
  | 'nav_installations'
  | 'nav_documentation'
  | 'nav_support'
  | 'nav_business_ui_labels'
  | 'nav_profile'
  | 'nav_logout'
  | 'admin_header_subtitle'
  // ── Mobile app labels (mob_* prefix) ──────────────────────────────────────
  | 'mob_tab_checks'
  | 'mob_tab_dashboard'
  | 'mob_tab_calendar'
  | 'mob_tab_schedule'
  | 'mob_tab_more'
  | 'mob_more_section_admin_tools'
  | 'mob_more_section_data_offline'
  | 'mob_more_card_due_checks'
  | 'mob_more_card_assets'
  | 'mob_more_card_locations'
  | 'mob_more_card_installations'
  // ── Mobile Dashboard Quick Access buttons ─────────────────────────────────
  | 'mob_qa_new_check'
  | 'mob_qa_incidents'
  | 'mob_qa_assign_tasks'
  | 'mob_qa_reassign_check'
  | 'mob_qa_add_asset'
  | 'mob_qa_deliveries'
  | 'mob_qa_dvla_management'
  | 'mob_qa_update_location'
  | 'mob_qa_view_active_loans'
  | 'mob_qa_section_title'
  // ── Calendar schedule overlays (web + mobile) ───────────────────────────────
  | 'cal_overlay_bookings'
  | 'cal_overlay_safety_checks'
  | 'cal_overlay_loan_returns'
  | 'cal_overlay_outgoing_deliveries';

export type AdminUiLabelGroup = 'web' | 'mobile' | 'calendar';

/** Calendar schedule overlay kinds that support custom labels (web + mobile). */
export type CalendarOverlayLabelKind =
  | 'booking'
  | 'safety_check'
  | 'loan_return'
  | 'outgoing_delivery';

export const ADMIN_UI_LABEL_DEFINITIONS: {
  key: AdminUiLabelKey;
  defaultLabel: string;
  description: string;
  group: AdminUiLabelGroup;
}[] = [
  // ── Web admin ─────────────────────────────────────────────────────────────
  {
    key: 'admin_header_subtitle',
    defaultLabel: 'Admin Dashboard',
    description: 'Top bar — shown after the business name (e.g. "Acme Ltd — …").',
    group: 'web',
  },
  { key: 'nav_dashboard', defaultLabel: 'Dashboard', description: 'Sidebar — main admin home.', group: 'web' },
  { key: 'nav_assets', defaultLabel: 'Assets', description: 'Sidebar — assets section.', group: 'web' },
  { key: 'nav_calendar', defaultLabel: 'Calendar', description: 'Sidebar — calendar view (when Calendar feature is enabled).', group: 'web' },
  { key: 'nav_calendar_bookings', defaultLabel: 'All bookings', description: 'Sidebar — bookings list under Calendar.', group: 'web' },
  { key: 'nav_calendar_settings', defaultLabel: 'Calendar settings', description: 'Sidebar — calendar configuration under Calendar.', group: 'web' },
  { key: 'nav_safety_checks', defaultLabel: 'Safety Checks', description: 'Sidebar — shown when Safety Checks feature is enabled.', group: 'web' },
  { key: 'nav_loan_checks', defaultLabel: 'Loan Checks', description: 'Sidebar — shown when Loan Checks feature is enabled.', group: 'web' },
  { key: 'nav_delivery_checks', defaultLabel: 'Delivery Checks', description: 'Sidebar — shown when Delivery Checks feature is enabled.', group: 'web' },
  { key: 'nav_location_checks', defaultLabel: 'Location Checks', description: 'Sidebar — shown when Location Checks is enabled. Overridden by Business UI labels; otherwise "Site Checks" when Sites terminology is on.', group: 'web' },
  { key: 'nav_locations', defaultLabel: 'Locations', description: 'Sidebar — locations / sites management. Overridden by Business UI labels; otherwise "Sites" when Sites terminology is on.', group: 'web' },
  { key: 'nav_templates', defaultLabel: 'Templates', description: 'Sidebar — check templates.', group: 'web' },
  { key: 'nav_communications', defaultLabel: 'Communications', description: 'Sidebar — communications module (schedules, round-ups, alerts).', group: 'web' },
  { key: 'nav_reports', defaultLabel: 'Reports', description: 'Sidebar — reports.', group: 'web' },
  { key: 'nav_dvla', defaultLabel: 'DVLA', description: 'Sidebar — DVLA section (when enabled).', group: 'web' },
  { key: 'nav_incidents', defaultLabel: 'Incidents', description: 'Sidebar — incidents (when enabled).', group: 'web' },
  { key: 'nav_hr', defaultLabel: 'HR', description: 'Sidebar — HR module (when enabled).', group: 'web' },
  { key: 'nav_mot_tax', defaultLabel: 'MOT / Tax', description: 'Sidebar — MOT & tax tracking (when enabled).', group: 'web' },
  { key: 'nav_vehicle_evaluation', defaultLabel: 'Vehicle Evaluation', description: 'Sidebar — vehicle valuations (when enabled).', group: 'web' },
  { key: 'nav_installations', defaultLabel: 'Installations', description: 'Sidebar — installations.', group: 'web' },
  { key: 'nav_push_notifications', defaultLabel: 'Push Notifications', description: 'Sidebar — push notifications.', group: 'web' },
  { key: 'nav_settings', defaultLabel: 'Settings', description: 'Sidebar — system settings.', group: 'web' },
  { key: 'nav_documentation', defaultLabel: 'Documentation', description: 'Sidebar — documentation link (Test Business only).', group: 'web' },
  { key: 'nav_support', defaultLabel: 'Support', description: 'Sidebar — support tickets.', group: 'web' },
  {
    key: 'nav_business_ui_labels',
    defaultLabel: 'Business UI labels',
    description: 'Sidebar — edit labels link (shown only while impersonating).',
    group: 'web',
  },
  { key: 'nav_profile', defaultLabel: 'Profile', description: 'Top bar — profile button.', group: 'web' },
  { key: 'nav_logout', defaultLabel: 'Logout', description: 'Sidebar — logout item.', group: 'web' },
  // ── Mobile app ────────────────────────────────────────────────────────────
  { key: 'mob_tab_checks', defaultLabel: 'Checks', description: 'Tab bar — Checks tab.', group: 'mobile' },
  { key: 'mob_tab_dashboard', defaultLabel: 'Dashboard', description: 'Tab bar — Dashboard tab.', group: 'mobile' },
  { key: 'mob_tab_calendar', defaultLabel: 'Calendar', description: 'Tab bar — Calendar / Bookings tab (when Calendar feature is enabled).', group: 'mobile' },
  { key: 'mob_tab_schedule', defaultLabel: 'Schedule', description: 'Tab bar — Schedule tab (HR module only).', group: 'mobile' },
  { key: 'mob_tab_more', defaultLabel: 'More', description: 'Tab bar — More tab.', group: 'mobile' },
  { key: 'mob_more_section_admin_tools', defaultLabel: 'Admin Tools', description: 'More screen — "Admin Tools" section heading.', group: 'mobile' },
  { key: 'mob_more_section_data_offline', defaultLabel: 'Data & offline', description: 'More screen — "Data & offline" section heading.', group: 'mobile' },
  { key: 'mob_more_card_due_checks', defaultLabel: 'Due checks', description: 'More screen — Due checks card title.', group: 'mobile' },
  { key: 'mob_more_card_assets', defaultLabel: 'Assets', description: 'More screen — Assets card title.', group: 'mobile' },
  { key: 'mob_more_card_locations', defaultLabel: 'Locations', description: 'More screen — locations card title.', group: 'mobile' },
  { key: 'mob_more_card_installations', defaultLabel: 'Installations', description: 'More screen — Installations card title.', group: 'mobile' },
  // Dashboard Quick Access
  { key: 'mob_qa_section_title', defaultLabel: 'Quick Access', description: 'Dashboard — "Quick Access" section heading.', group: 'mobile' },
  { key: 'mob_qa_new_check', defaultLabel: 'New Check', description: 'Dashboard Quick Access — start a new check.', group: 'mobile' },
  { key: 'mob_qa_incidents', defaultLabel: 'Incidents', description: 'Dashboard Quick Access — log an incident.', group: 'mobile' },
  { key: 'mob_qa_assign_tasks', defaultLabel: 'Assign Tasks', description: 'Dashboard Quick Access — assign a check to someone.', group: 'mobile' },
  { key: 'mob_qa_reassign_check', defaultLabel: 'Reassign Check', description: 'Dashboard Quick Access — reassign an existing check.', group: 'mobile' },
  { key: 'mob_qa_add_asset', defaultLabel: 'Add Asset', description: 'Dashboard Quick Access — add a new asset.', group: 'mobile' },
  { key: 'mob_qa_deliveries', defaultLabel: 'Deliveries', description: 'Dashboard Quick Access — deliveries (when enabled).', group: 'mobile' },
  { key: 'mob_qa_dvla_management', defaultLabel: 'DVLA Management', description: 'Dashboard Quick Access — DVLA management (when enabled).', group: 'mobile' },
  { key: 'mob_qa_update_location', defaultLabel: 'Update Location', description: 'Dashboard Quick Access — update asset location.', group: 'mobile' },
  { key: 'mob_qa_view_active_loans', defaultLabel: 'View Active Loans', description: 'Dashboard Quick Access — view active loans (when enabled).', group: 'mobile' },
  // ── Calendar (web admin + mobile app) ─────────────────────────────────────
  {
    key: 'cal_overlay_bookings',
    defaultLabel: 'Bookings',
    description: 'Calendar — filter label for internal bookings on the schedule.',
    group: 'calendar',
  },
  {
    key: 'cal_overlay_safety_checks',
    defaultLabel: 'Checks',
    description: 'Calendar — filter label for scheduled checks.',
    group: 'calendar',
  },
  {
    key: 'cal_overlay_loan_returns',
    defaultLabel: 'Loan returns',
    description: 'Calendar — filter label for loan return due dates.',
    group: 'calendar',
  },
  {
    key: 'cal_overlay_outgoing_deliveries',
    defaultLabel: 'Outgoing deliveries',
    description: 'Calendar — filter label for outgoing delivery schedules.',
    group: 'calendar',
  },
];

const CALENDAR_OVERLAY_LABEL_KEY_BY_KIND: Record<CalendarOverlayLabelKind, AdminUiLabelKey> = {
  booking: 'cal_overlay_bookings',
  safety_check: 'cal_overlay_safety_checks',
  loan_return: 'cal_overlay_loan_returns',
  outgoing_delivery: 'cal_overlay_outgoing_deliveries',
};

export function resolveCalendarOverlayLabel(
  kind: CalendarOverlayLabelKind,
  overrides: Partial<Record<AdminUiLabelKey, string>> | null | undefined,
): string {
  return resolveAdminUiLabel(CALENDAR_OVERLAY_LABEL_KEY_BY_KIND[kind], overrides);
}

export const DEFAULT_ADMIN_UI_LABELS: Record<AdminUiLabelKey, string> =
  ADMIN_UI_LABEL_DEFINITIONS.reduce((acc, row) => {
    acc[row.key] = row.defaultLabel;
    return acc;
  }, {} as Record<AdminUiLabelKey, string>);

export function parseAdminUiLabels(raw: unknown): Partial<Record<AdminUiLabelKey, string>> {
  if (raw == null) return {};
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return {};
  const out: Partial<Record<AdminUiLabelKey, string>> = {};
  const allowed = new Set(ADMIN_UI_LABEL_DEFINITIONS.map((d) => d.key));
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (!allowed.has(k as AdminUiLabelKey)) continue;
    if (typeof v !== 'string') continue;
    const t = v.trim().slice(0, 120);
    if (t) out[k as AdminUiLabelKey] = t;
  }
  return out;
}

export function resolveAdminUiLabel(
  key: AdminUiLabelKey,
  overrides: Partial<Record<AdminUiLabelKey, string>> | null | undefined
): string {
  const o = overrides?.[key];
  if (typeof o === 'string' && o.trim()) return o.trim();
  return DEFAULT_ADMIN_UI_LABELS[key];
}

/**
 * Derives the "Templates" tab/sub-item label from a check-module nav label.
 * "Safety Checks" → "Safety Templates"
 * "Vehicle Inspections" → "Vehicle Inspections Templates"
 */
export function resolveTemplateTabLabel(
  checkNavKey: AdminUiLabelKey,
  overrides: Partial<Record<AdminUiLabelKey, string>> | null | undefined
): string {
  const checkLabel = resolveAdminUiLabel(checkNavKey, overrides);
  const base = checkLabel.endsWith(' Checks') ? checkLabel.slice(0, -7) : checkLabel;
  return `${base} Templates`;
}

/**
 * Derives the singular check-type label from a check-module nav label.
 * "Safety Checks" → "Safety Check"
 * "Vehicle Inspections" → "Vehicle Inspection"
 */
export function resolveCheckSingularLabel(
  checkNavKey: AdminUiLabelKey,
  overrides: Partial<Record<AdminUiLabelKey, string>> | null | undefined
): string {
  const checksLabel = resolveAdminUiLabel(checkNavKey, overrides);
  // Strip trailing 's' to make plural → singular ("Checks" → "Check", "Inspections" → "Inspection")
  return checksLabel.endsWith('s') ? checksLabel.slice(0, -1) : checksLabel;
}

/**
 * Resolves a human-readable label for a raw check_type DB enum value.
 * Uses business custom labels when available; falls back to sensible defaults.
 */
export function resolveCheckTypeEnumLabel(
  checkType: string | null | undefined,
  overrides: Partial<Record<AdminUiLabelKey, string>> | null | undefined
): string {
  const type = (checkType ?? '').toUpperCase();
  const base = (key: AdminUiLabelKey) => {
    const label = resolveAdminUiLabel(key, overrides);
    return label.endsWith(' Checks') ? label.slice(0, -7) : label;
  };
  switch (type) {
    case 'SAFETY': return base('nav_safety_checks');
    case 'LOAN': return base('nav_loan_checks');
    case 'LOAN_RETURN': return `${base('nav_loan_checks')} Return`;
    case 'DELIVERY': return base('nav_delivery_checks');
    case 'OUTGOING_DELIVERY': return `Outgoing ${base('nav_delivery_checks')}`;
    case 'LOCATION': return base('nav_location_checks');
    case 'DVLA': return 'DVLA';
    default: return type || 'Unknown';
  }
}
